/**
 * DelegationsPage — les délégations de brique de la commune (mig 255, 15.6).
 *
 * ─── CE QUE LE MAIRE FAIT ICI ───────────────────────────────────────────────
 *
 * Déclarer qu'une brique (cantine · périscolaire · ALSH · petite enfance) est
 * exercée par quelqu'un d'autre : la communauté de communes, ou un prestataire.
 *
 * ─── LE POINT QUI COMPTE, ET QU'IL FAUT DIRE À L'ÉCRAN ──────────────────────
 *
 * Le RÉGIME n'est pas une case administrative : il détermine qui est
 * responsable de traitement au sens du RGPD, donc quelles mentions les familles
 * liront et à quel DPO leurs demandes seront routées. L'écran explique donc
 * chaque régime en clair, et affiche la conséquence à mesure qu'on choisit.
 *
 * ⚠️ Le responsable est PRÉ-REMPLI d'après le régime, ajustable à la création,
 * et figé ensuite : le changer après coup rendrait fausses des mentions déjà
 * lues et des documents déjà émis.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Alert, Button, Input, Field, Badge, ModuleHeader } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { AppHeader } from '@/components/AppHeader';
import { api, ApiException } from '@/lib/api';
import type {
  BriqueDelegation, RegimeDelegation, ResponsableTraitement,
} from '@roronoa-collectivites/shared-types';
import { Handshake, Plus, History, Info, Loader2, X, ScrollText } from 'lucide-react';

const BRIQUES: { key: string; label: string }[] = [
  { key: 'scolaire_cantine', label: 'Cantine scolaire' },
  { key: 'periscolaire', label: 'Périscolaire (garderie)' },
  { key: 'alsh', label: 'ALSH (mercredis et vacances)' },
  { key: 'petite_enfance', label: 'Petite enfance' },
];

const REGIMES: { key: RegimeDelegation; label: string; aide: string }[] = [
  {
    key: 'transfert_total', label: 'Transfert total de compétence',
    aide: 'La commune ne décide plus rien et ne budgète plus rien sur ce champ (CGCT L. 5211-5 III). Le délégataire devient seul responsable des données.',
  },
  {
    key: 'partiel_mad', label: 'Compétence partielle avec mise à disposition',
    aide: 'La commune garde une part de décision et met des moyens à disposition. Responsabilité partagée par défaut.',
  },
  {
    key: 'service_commun', label: 'Service commun (L. 5211-4-2)',
    aide: 'Un service mutualisé travaille pour plusieurs collectivités. Responsabilité partagée par défaut.',
  },
  {
    key: 'delegation_L1111_8', label: 'Délégation de compétence (L. 1111-8)',
    aide: 'La commune délègue l\'exercice mais garde la main sur les finalités : elle reste responsable des données.',
  },
  {
    key: 'prestataire_prive', label: 'Prestataire privé',
    aide: 'Un prestataire exécute pour le compte de la commune : sous-traitant au sens de l\'article 28. La commune reste responsable.',
  },
];

const RESPONSABLE_LABELS: Record<ResponsableTraitement, string> = {
  delegataire: 'Le délégataire est seul responsable',
  commune: 'La commune reste responsable',
  conjoint: 'Responsabilité conjointe',
};

const RESPONSABLE_PAR_REGIME: Record<RegimeDelegation, ResponsableTraitement> = {
  transfert_total: 'delegataire',
  partiel_mad: 'conjoint',
  service_commun: 'conjoint',
  delegation_L1111_8: 'commune',
  prestataire_prive: 'commune',
};

export function DelegationsPage() {
  const { confirm, dialogElement } = useConfirmDialog();

  const [delegations, setDelegations] = useState<BriqueDelegation[]>([]);
  const [historique, setHistorique] = useState(false);
  const [delegataires, setDelegataires] = useState<{ id: string; nom: string; type: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [formOuvert, setFormOuvert] = useState(false);

  const [journal, setJournal] = useState<{
    id: string; brique: string; methode: string; chemin: string;
    agent: string; delegataire: string; createdAt: string;
  }[] | null>(null);
  const [journalOuvert, setJournalOuvert] = useState(false);

  const [brique, setBrique] = useState('scolaire_cantine');
  const [delegataireId, setDelegataireId] = useState('');
  const [regime, setRegime] = useState<RegimeDelegation>('transfert_total');
  const [responsable, setResponsable] = useState<ResponsableTraitement>('delegataire');
  const [dateDebut, setDateDebut] = useState('');
  const [note, setNote] = useState('');

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [d, dp] = await Promise.all([
        api.delegations.list(historique),
        api.delegations.delegatairesPossibles().catch(() => []),
      ]);
      setDelegations(d); setDelegataires(dp);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, [historique]);
  useEffect(() => { void charger(); }, [charger]);

  // Le responsable suit le régime tant que le maire n'y a pas touché lui-même.
  function choisirRegime(r: RegimeDelegation) {
    setRegime(r);
    setResponsable(RESPONSABLE_PAR_REGIME[r]);
  }

  async function creer() {
    setSaving(true); setError(null); setMsg(null);
    try {
      await api.delegations.creer({
        brique,
        delegataireTenantId: delegataireId || null,
        regime,
        responsableTraitement: responsable,
        dateDebut,
        note: note.trim() || null,
      });
      setMsg('Délégation enregistrée.');
      setFormOuvert(false);
      setDelegataireId(''); setNote(''); setDateDebut('');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setSaving(false); }
  }

  async function terminer(d: BriqueDelegation) {
    const ok = await confirm({
      title: 'Reprendre cette compétence ?',
      description: `${d.delegataireNom} n'aura plus accès à cette brique dès validation. Les journaux d'accès sont conservés, et aucun compte n'est supprimé.`,
      confirmLabel: 'Reprendre la compétence',
      danger: true,
    });
    if (!ok) return;
    setSaving(true); setError(null);
    try {
      await api.delegations.terminer(d.id);
      setMsg('Compétence reprise par la commune.');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Opération impossible');
    } finally { setSaving(false); }
  }

  const pretACreer = !!delegataireId && !!dateDebut;
  const regimeChoisi = REGIMES.find((r) => r.key === regime);

  return (
    <div className="min-h-screen text-ink">
      {/* Le bandeau mairie manquait : sans lui, aucun retour au tableau de bord
          depuis ces écrans. Remonté par la mairie le 28/07. */}
      <AppHeader module="Administration" accent="rh" />
      <ModuleHeader accent="rh" icon={Handshake} eyebrow="Administration"
        title="Délégations de compétence" />

      <main id="main-content" className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        {error && <Alert variant="error" className="mb-4">{error}</Alert>}
        {msg && <Alert variant="success" className="mb-4">{msg}</Alert>}

        <Alert variant="info" className="mb-4">
          Déclarer une délégation ne déplace aucune donnée : elles restent celles de
          la commune. La délégation ouvre un accès au délégataire et détermine les
          mentions légales affichées aux familles.
        </Alert>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Button variant="primary" size="sm" onClick={() => setFormOuvert((v) => !v)}>
            {formOuvert ? <X className="h-4 w-4" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
            {formOuvert ? 'Annuler' : 'Déléguer une brique'}
          </Button>
          <Button variant="glass" size="sm" onClick={() => setHistorique((v) => !v)}>
            <History className="h-4 w-4" aria-hidden="true" />
            {historique ? 'Masquer l\'historique' : 'Voir l\'historique'}
          </Button>
          {/* Le registre des accès : c'est lui qui départage en cas de litige.
              Lectures comprises — consulter la liste des enfants EST un accès. */}
          <Button
            variant="glass" size="sm"
            onClick={async () => {
              const ouvrir = !journalOuvert;
              setJournalOuvert(ouvrir);
              if (ouvrir && journal === null) {
                try { setJournal(await api.delegations.journal()); }
                catch { setJournal([]); }
              }
            }}
          >
            <ScrollText className="h-4 w-4" aria-hidden="true" />
            {journalOuvert ? 'Masquer le journal' : 'Journal des accès'}
          </Button>
        </div>

        {formOuvert && (
          <Card variant="glass" padded className="mb-4 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Brique déléguée" htmlFor="del-brique" required>
                <select id="del-brique" value={brique} onChange={(e) => setBrique(e.target.value)}
                  className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                  {BRIQUES.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
              </Field>

              <Field label="Délégataire" htmlFor="del-qui" required
                hint="Un EPCI ou un syndicat — pas une commune voisine">
                <select id="del-qui" value={delegataireId} onChange={(e) => setDelegataireId(e.target.value)}
                  className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                  <option value="">— Choisir —</option>
                  {delegataires.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
                </select>
              </Field>
            </div>

            <fieldset>
              <legend className="mb-2 text-sm font-medium text-ink">Régime juridique</legend>
              <div className="space-y-2">
                {REGIMES.map((r) => (
                  <button
                    key={r.key} type="button" onClick={() => choisirRegime(r.key)}
                    aria-pressed={regime === r.key}
                    className={`w-full rounded-r-lg border p-3 text-left transition-colors ${
                      regime === r.key ? 'border-tech/50 bg-tech/10' : 'border-border bg-surface/50 hover:border-border-strong'
                    }`}
                  >
                    <span className="block text-sm font-medium text-ink">{r.label}</span>
                    <span className="mt-0.5 block text-xs text-ink-muted">{r.aide}</span>
                  </button>
                ))}
              </div>
            </fieldset>

            <Card variant="solid" padded>
              <p className="flex items-start gap-2 text-sm text-ink">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-subtle" aria-hidden="true" />
                <span>
                  Conséquence RGPD : <strong>{RESPONSABLE_LABELS[responsable]}</strong>.
                  <span className="mt-1 block text-xs text-ink-muted">
                    Déduit du régime choisi. Vous pouvez l'ajuster maintenant si les
                    faits diffèrent de la convention — la CNIL qualifie par les faits —
                    mais il sera figé après enregistrement.
                  </span>
                </span>
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(['delegataire', 'conjoint', 'commune'] as const).map((v) => (
                  <button
                    key={v} type="button" onClick={() => setResponsable(v)}
                    aria-pressed={responsable === v}
                    className={`h-8 rounded-r-pill border px-3 text-xs ${
                      responsable === v ? 'border-tech/50 bg-tech/10 font-medium text-ink' : 'border-border bg-surface/50 text-ink-muted'
                    }`}
                  >
                    {RESPONSABLE_LABELS[v]}
                  </button>
                ))}
              </div>
            </Card>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Date de début" htmlFor="del-debut" required>
                <Input id="del-debut" type="date" value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)} />
              </Field>
              <Field label="Référence de l'acte" htmlFor="del-note"
                hint="Arrêté préfectoral, délibération, convention">
                <Input id="del-note" value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Arrêté préfectoral n° …" />
              </Field>
            </div>

            <div className="border-t border-border pt-4">
              <Button variant="primary" loading={saving} disabled={!pretACreer} onClick={() => void creer()}>
                Enregistrer la délégation
              </Button>
              {regimeChoisi && (
                <p className="mt-2 text-xs text-ink-subtle">{regimeChoisi.aide}</p>
              )}
            </div>
          </Card>
        )}

        {journalOuvert && (
          <Card variant="glass" padded className="mb-4">
            <h2 className="font-medium text-ink">Journal des accès délégués</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Chaque requête menée par un délégataire sur vos données, <strong>consultations
              comprises</strong>. Ce registre vous appartient et survit à la fin de la délégation.
            </p>
            {journal === null ? (
              <p className="mt-3 text-sm text-ink-subtle">Chargement…</p>
            ) : journal.length === 0 ? (
              <p className="mt-3 text-sm text-ink-subtle">Aucun accès délégué à ce jour.</p>
            ) : (
              <ul className="mt-3 divide-y divide-border">
                {journal.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-sm">
                    <span className="text-ink">
                      {a.agent} <span className="text-ink-subtle">· {a.delegataire}</span>
                    </span>
                    <span className="font-mono text-xs text-ink-muted">{a.methode} {a.chemin}</span>
                    <span className="text-xs text-ink-subtle">
                      {new Intl.DateTimeFormat('fr-FR', {
                        dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Paris',
                      }).format(new Date(a.createdAt))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-12 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
          </div>
        ) : delegations.length === 0 ? (
          <Card variant="glass" padded>
            <p className="text-sm text-ink-muted">
              Aucune délégation{historique ? '' : ' en cours'}. La commune exerce
              elle-même toutes ses compétences.
            </p>
          </Card>
        ) : (
          <ul className="space-y-2">
            {delegations.map((d) => (
              <li key={d.id}>
                <Card variant="glass" padded>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-ink">
                          {BRIQUES.find((b) => b.key === d.brique)?.label ?? d.brique}
                        </span>
                        <Badge variant={d.statut === 'active' ? 'neutral' : 'urgent'} size="sm">
                          {d.statut === 'active' ? 'En cours' : 'Terminée'}
                        </Badge>
                      </div>
                      <div className="mt-0.5 text-sm text-ink-muted">
                        Exercée par <strong className="text-ink">{d.delegataireNom}</strong>
                      </div>
                      <div className="mt-1 text-xs text-ink-subtle">
                        {REGIMES.find((r) => r.key === d.regime)?.label ?? d.regime}
                        {' · '}{RESPONSABLE_LABELS[d.responsableTraitement]}
                      </div>
                      <div className="mt-1 text-xs text-ink-subtle">
                        Depuis le {d.dateDebut}{d.dateFin && ` — jusqu'au ${d.dateFin}`}
                      </div>
                      {d.note && <p className="mt-1 text-xs text-ink-muted">{d.note}</p>}
                    </div>

                    {d.statut === 'active' && (
                      <Button variant="glass" size="sm" disabled={saving} onClick={() => void terminer(d)}>
                        Reprendre la compétence
                      </Button>
                    )}
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </main>
      {dialogElement}
    </div>
  );
}
